import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Check, ArrowRight, Clock } from "lucide-react";
import FeatureHint from "@/components/FeatureHint";
import { useAuth } from "@/context/AuthContext";

/**
 * ProfileCompletionWidget — gamified 2-step progress bar (Email · Phone).
 *
 * Rendered on the Dashboard. Auto-hides once the user reaches 100 %.
 */
export default function ProfileCompletionWidget() {
    const { user } = useAuth();
    const [state, setState] = useState(null);

    async function load() {
        try {
            const { data } = await api.get("/profile/completion");
            setState(data);
        } catch { /* silent */ }
    }

    // Re-fetch whenever a profile field changes (ProfileEditModal calls the
    // auth context's `refresh()` on save) — otherwise this kept showing the
    // OLD percentage/step after the user had already finished it. See the
    // matching fix in OnboardingGateContext.jsx for the same root cause.
    useEffect(() => { load(); }, [user?.phone, user?.nationality, user?.country_of_residence, user?.is_email_verified]);

    if (!state) return null;
    // Full 100 % — dismiss to keep the dashboard clean.
    if (state.score >= 100) return null;

    // Pick the first unfinished item as the primary CTA.
    const nextItem = state.items.find((it) => !it.done);

    return (
        <div
            className="mb-6 border border-border bg-card rounded-lg p-5 card-hover"
            data-testid="profile-completion-widget"
        >
            <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
                <div>
                    <div className="overline text-primary flex items-center gap-1.5">
                        Get set up
                        <FeatureHint
                            title="Why complete your profile?"
                            body="Each step below unlocks a real capability — verified email keeps your account recoverable, and a Kenyan phone number powers in-app notifications."
                            bullets={[
                                "100% complete = ready for payouts and public leader status.",
                                "Takes ~2 minutes end-to-end.",
                                "Widget auto-hides once you reach 100%.",
                            ]}
                            testId="hint-profile-completion"
                        />
                    </div>
                    <div className="text-lg font-extrabold tracking-tight">
                        Your profile is {state.score}% complete
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                        {state.completed} of {state.total} steps done — keeps your account recoverable.
                    </div>
                </div>
                {nextItem && (
                    <Link
                        to={nextItem.cta_route}
                        data-testid="profile-completion-next-cta"
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline"
                    >
                        {nextItem.cta_label}
                        <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                )}
            </div>

            {/* Segmented progress bar — 1 pill per step */}
            <div className="grid grid-cols-2 gap-1.5 mb-4" data-testid="profile-completion-bar">
                {state.items.map((it) => (
                    <div
                        key={it.key}
                        className={`h-1.5 rounded-full transition-colors ${
                            it.done
                                ? "bg-success"
                                : it.pending
                                    ? "bg-amber-500"
                                    : "bg-secondary"
                        }`}
                        aria-label={`${it.label} · ${it.done ? "done" : it.pending ? "pending" : "todo"}`}
                    />
                ))}
            </div>

            {/* Step list */}
            <div className="grid sm:grid-cols-2 gap-2">
                {state.items.map((it) => (
                    <ProfileStep key={it.key} item={it} />
                ))}
            </div>
        </div>
    );
}

function ProfileStep({ item }) {
    return (
        <Link
            to={item.done ? "#" : item.cta_route}
            onClick={(e) => item.done && e.preventDefault()}
            data-testid={`profile-step-${item.key}`}
            className={`flex items-center gap-3 rounded-md border p-3 transition-colors ${
                item.done
                    ? "border-success/30 bg-success/5 cursor-default"
                    : item.pending
                        ? "border-amber-500/40 bg-amber-500/10 hover:border-amber-500/60"
                        : "border-border hover:border-primary/60 hover:bg-primary/5"
            }`}
        >
            <div
                className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                    item.done
                        ? "bg-success/20 text-success"
                        : item.pending
                            ? "bg-amber-500/20 text-amber-600"
                            : "bg-secondary text-muted-foreground"
                }`}
            >
                {item.done ? (
                    <Check className="w-4 h-4" />
                ) : item.pending ? (
                    <Clock className="w-4 h-4" />
                ) : (
                    <span className="text-[10px] font-bold">•</span>
                )}
            </div>
            <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold truncate">{item.label}</div>
                <div className="text-[11px] text-muted-foreground truncate">{item.hint}</div>
            </div>
        </Link>
    );
}
